import { useEffect } from 'react';
import { formatCallDuration } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner } from '../../ds/components';
import { usePrefersReducedMotion } from '../../ds/hooks';
import { elapsedSeconds, formatCallTimer, isLive } from './callState';
import { LevelMeter } from './LevelMeter';
import { useCall, useClock } from './useCall';
import styles from './CallBar.module.css';

/**
 * P-45 — the softphone controls of the contact record's call editor (E1 has no call controls in the
 * design): the navy strip of the E1 title bar and the dialer's queue bar with „Anrufen" · „wählt …" /
 * the mm:ss timer · the level meter · „Stumm" · „Auflegen". Without telephony the button is disabled with
 * the server's reason and the editor keeps the manual log (D-47).
 */

export interface CallBarProps {
  contactId: string;
  /** „+49 171 482 93 07" — shown while idle. */
  phoneDisplay: string | null;
  /** The open call task the editor was opened from. */
  taskId?: string | null | undefined;
  /** Why no call can be started (not configured, no number, DNC); null = ready. */
  disabledReason: string | null;
}

export function CallBar({ contactId, phoneDisplay, taskId, disabledReason }: CallBarProps) {
  const call = useCall();
  const { state } = call;
  const reducedMotion = usePrefersReducedMotion();
  const mine = state.contactId === contactId && state.source === 'record';
  const phase = mine ? state.phase : 'idle';
  const live = isLive(phase);
  const now = useClock(phase === 'connected');
  const seconds = mine ? elapsedSeconds(state, now) : null;
  const timer = seconds === null ? '00:00' : formatCallTimer(seconds * 1000);

  const { prepare } = call;
  useEffect(() => {
    if (disabledReason === null) prepare();
  }, [disabledReason, prepare]);

  let status: string;
  switch (phase) {
    case 'dialing':
      status = state.ringing ? 'klingelt …' : 'wählt …';
      break;
    case 'connected':
      status = timer;
      break;
    case 'ended':
      status =
        seconds === null
          ? 'Nicht erreicht'
          : `Gespräch beendet · ${formatCallDuration(state.view?.durationSeconds ?? seconds)}`;
      break;
    case 'failed':
      status = 'Verbindung fehlgeschlagen';
      break;
    case 'idle':
      status = phoneDisplay ?? 'Keine Telefonnummer hinterlegt.';
      break;
  }

  // Screen readers hear the state changes and the timer every 10 seconds, not every second.
  const announcement =
    phase === 'connected'
      ? `Verbunden · ${formatCallTimer(Math.floor((seconds ?? 0) / 10) * 10 * 1000)}`
      : phase === 'idle'
        ? ''
        : status;

  const error = state.error !== null && (mine || !isLive(state.phase)) ? state.error : null;

  return (
    <div className={styles.wrap}>
      <div className={clsx(styles.bar, live && styles.live)} role="group" aria-label="Softphone">
        <span
          className={clsx(styles.dot, live && !reducedMotion && styles.dotPulse, !live && styles.dotIdle)}
          aria-hidden="true"
        />
        <span className={clsx(styles.status, phase === 'connected' && styles.timer)}>{status}</span>
        {live ? (
          <LevelMeter
            local={state.localStream}
            remote={state.remoteStream}
            active={phase === 'connected'}
            muted={state.muted}
          />
        ) : null}
        <span className={styles.actions}>
          {live ? (
            <>
              <button
                type="button"
                className={styles.ghost}
                aria-pressed={state.muted}
                disabled={phase !== 'connected'}
                onClick={() => call.mute(!state.muted)}
              >
                Stumm
              </button>
              <button type="button" className={styles.hangup} onClick={call.hangup}>
                Auflegen
              </button>
            </>
          ) : phase === 'idle' || phase === 'failed' ? (
            <button
              type="button"
              className={styles.dial}
              disabled={disabledReason !== null}
              title={disabledReason ?? undefined}
              onClick={() => {
                call.dismissError();
                void call.start({ contactId, source: 'record', taskId: taskId ?? undefined });
              }}
            >
              Anrufen
            </button>
          ) : null}
        </span>
        <span className={styles.srOnly} aria-live="polite">
          {announcement}
        </span>
      </div>
      {disabledReason !== null && phase === 'idle' ? <p className={styles.reason}>{disabledReason}</p> : null}
      {error !== null ? (
        <Banner tone="danger" role="alert">
          {error}
        </Banner>
      ) : null}
    </div>
  );
}
